"use client";

import { useEffect, useRef, useState } from "react";

const SCANNER_REGION_ID = "badge-scanner-region";

/**
 * Camera-based barcode scanner overlay. Supports QR, Code 128, Code 39.
 * Uses the rear camera on tablets; falls back to any available camera.
 *
 * Calls onScanned(value) once on first successful decode, then unmounts via
 * the parent removing this component.
 */
export function BadgeScanner({
  onScanned,
  onClose,
}: {
  onScanned: (value: string) => void;
  onClose: () => void;
}) {
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(true);
  // Lazy-imported instance; typed loosely to avoid pulling html5-qrcode types
  // into our project.
  const scannerRef = useRef<{
    stop: () => Promise<void>;
    clear: () => void;
  } | null>(null);
  // Guard against firing onScanned twice if the library decodes multiple frames
  // while we're tearing down.
  const handledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const mod = await import("html5-qrcode");
        if (cancelled) return;

        const { Html5Qrcode, Html5QrcodeSupportedFormats } = mod;

        // Pick the best available camera: prefer rear-facing on tablets,
        // fall back to any camera (laptops typically only have a front cam).
        let cameraConstraint: MediaTrackConstraints | string;
        try {
          const devices = await Html5Qrcode.getCameras();
          if (cancelled) return;
          if (!devices || devices.length === 0) {
            throw new Error("No camera detected on this device.");
          }
          // Look for a label hinting at rear/back/environment camera.
          const rear = devices.find((d) =>
            /(back|rear|environment)/i.test(d.label || "")
          );
          const chosen = rear || devices[0];
          cameraConstraint = { deviceId: { exact: chosen.id } };
        } catch (e) {
          // Some browsers/contexts reject getCameras() unless permission is
          // already granted. Fall back to facingMode hint so the browser
          // prompts and picks something usable.
          if (e instanceof Error && /no camera/i.test(e.message)) throw e;
          cameraConstraint = { facingMode: "environment" };
        }

        const scanner = new Html5Qrcode(SCANNER_REGION_ID, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
          ],
          verbose: false,
        });

        await scanner.start(
          cameraConstraint,
          { fps: 10, qrbox: { width: 280, height: 200 } },
          (decoded: string) => {
            if (handledRef.current) return;
            const value = decoded.trim();
            if (!value) return;
            handledRef.current = true;
            onScanned(value);
          },
          () => {
            // Per-frame decode failures are noisy and expected; ignore.
          }
        );

        if (cancelled) {
          await scanner.stop().catch(() => {});
          scanner.clear();
          return;
        }

        scannerRef.current = scanner;
        setStarting(false);
      } catch (e) {
        const raw = e instanceof Error ? e.message : "Could not start camera";
        // Map known DOMException names to friendlier messages.
        const name = (e as { name?: string })?.name || "";
        let friendly = raw;
        if (name === "NotAllowedError" || /denied|permission/i.test(raw)) {
          friendly = "Camera permission was denied. Allow camera access for this site, then try again.";
        } else if (name === "NotFoundError" || /no camera/i.test(raw)) {
          friendly = "No camera detected on this device.";
        } else if (name === "NotReadableError") {
          friendly = "Camera is in use by another application.";
        } else if (name === "OverconstrainedError") {
          friendly = "No camera matched the requested settings. Try a different device.";
        }
        setError(friendly);
        setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      const scanner = scannerRef.current;
      scannerRef.current = null;
      if (scanner) {
        scanner
          .stop()
          .then(() => scanner.clear())
          .catch(() => {});
      }
    };
    // onScanned is intentionally not in deps — we only want to start the
    // camera once when this component mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[var(--radius)] p-4 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-3">Scan badge</h3>
        <div
          id={SCANNER_REGION_ID}
          className="w-full overflow-hidden rounded-[var(--radius)] bg-black aspect-square"
        />
        {starting && (
          <p className="text-gray-500 text-sm mt-3">Starting camera…</p>
        )}
        {error && (
          <p className="text-red-600 text-sm mt-3">
            {error}
            <span className="block text-gray-500 mt-1">
              You may need to grant camera permission to this site.
            </span>
          </p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-[var(--radius)] font-medium"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
